import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft } from 'lucide-react';
import NumericKeypad from '../../../../../../../components/NumericKeypad';
import useIsMobile from '../../../../../../../hooks/useIsMobile';

const SaleModal = ({ lot, onClose, onConfirm }) => {
  const [title, setTitle] = useState(lot.title || '');
  const [quantity, setQuantity] = useState(lot.initialQuantity || '');
  const [unitPrice, setUnitPrice] = useState(lot.initialUnitPrice || lot.suggestedPrice?.toString() || '');
  const [deposit, setDeposit] = useState(lot.initialDeposit || '');
  const [isLoading, setIsLoading] = useState(false);
  const [activeInput, setActiveInput] = useState('quantity');
  const quantityInputRef = useRef(null);
  const isMobile = useIsMobile();

  const subtotal = (Number(quantity) || 0) * (Number(unitPrice) || 0);
  const totalDeposit = (Number(quantity) || 0) * (Number(deposit) || 0);

  // Focus quantity input on mount
  useEffect(() => {
    if (quantityInputRef.current) {
      quantityInputRef.current.focus();
    }
  }, []);

  const handleNumericInput = (value) => {
    switch (activeInput) {
      case 'quantity':
        setQuantity(prev => prev + value.toString());
        break;
      case 'unitPrice':
        setUnitPrice(prev => prev + value.toString());
        break;
      case 'deposit':
        setDeposit(prev => prev + value.toString());
        break;
    }
  };

  const handleInputChange = (e, setter) => {
    const value = e.target.value;
    // Solo permitir números y un punto decimal
    if (/^\d*\.?\d*$/.test(value)) {
      setter(value);
    }
  };

  const handleKeyDown = (e) => {
    // Si presiona Enter, moverse al siguiente campo o confirmar
    if (e.key === 'Enter') {
      e.preventDefault();
      if (hasNextInput()) {
        handleNext();
      } else if (!isConfirmDisabled) {
        handleConfirm();
      }
    }
  };

  const handleDelete = () => {
    switch (activeInput) {
      case 'quantity':
        setQuantity(prev => prev.slice(0, -1));
        break;
      case 'unitPrice':
        setUnitPrice(prev => prev.slice(0, -1));
        break;
      case 'deposit':
        setDeposit(prev => prev.slice(0, -1));
        break;
    }
  };

  const handleConfirm = async () => {
    if (isConfirmDisabled) return;
    
    setIsLoading(true);
    try {
      await onConfirm({
        lotId: lot.id,
        title: lot.is_lightning_lot ? title : lot.title,
        quantity: Number(quantity),
        unit: lot.unit || 'unidades',
        unitPrice: Number(unitPrice),
        deposit: Number(deposit),
        container_deposit: lot.container_deposit || 0,
        subtotal,
        totalDeposit,
        is_lightning_lot: lot.is_lightning_lot
      });
    } catch (error) {
      console.error('Error al confirmar:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNext = () => {
    switch (activeInput) {
      case 'quantity':
        setActiveInput('unitPrice');
        break;
      case 'unitPrice':
        setActiveInput('deposit');
        break;
      case 'deposit':
        // No hay siguiente, se queda en deposit
        break;
    }
  };

  const hasNextInput = () => {
    switch (activeInput) {
      case 'quantity':
      case 'unitPrice':
        return true;
      case 'deposit':
        return false;
      default:
        return false;
    }
  };

  const isConfirmDisabled = !quantity || !unitPrice || (lot.is_lightning_lot && !title);

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-25 dark:bg-opacity-50 z-20"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-x-0 bottom-0 bg-white dark:bg-gray-800 rounded-t-xl p-4 space-y-4 z-30">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              {lot.is_lightning_lot ? 'Venta relámpago' : lot.title}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {lot.is_lightning_lot ? 'Sin lote existente' : `#${lot.display_id}`}
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
            <ArrowLeft className="w-5 h-5" />
          </button>
        </div>

        {/* Campos */}
        <div className="space-y-4">
          {lot.is_lightning_lot && (
            <div>
              <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">
                Nombre del producto
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-full p-2 border dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                placeholder="Ingrese nombre del producto"
              />
            </div>
          )}

          {/* Grid de inputs */}
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">
                Cantidad a vender
              </label>
              <div className="text-xs text-gray-400 dark:text-gray-500 mb-1">
                {lot.is_lightning_lot ? '' : `Disponible: ${lot.quantity}`}
              </div>
              <input
                ref={quantityInputRef}
                type="text"
                inputMode="decimal"
                value={quantity}
                onChange={(e) => handleInputChange(e, setQuantity)}
                onFocus={() => setActiveInput('quantity')}
                onKeyDown={handleKeyDown}
                readOnly={isMobile}
                className={`w-full p-3 border-2 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-center text-lg ${activeInput === 'quantity' ? 'border-blue-500 dark:border-blue-400' : ''} read-only:bg-white dark:read-only:bg-gray-800`}
                placeholder="0"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">
                Precio unitario
              </label>
              <div className="text-xs text-gray-400 dark:text-gray-500 mb-1">
                {lot.suggestedPrice ? `Sugerido: $${lot.suggestedPrice}` : 'Sin precio sugerido'}
              </div>
              <input
                type="text"
                inputMode="decimal"
                value={unitPrice}
                onChange={(e) => handleInputChange(e, setUnitPrice)}
                onFocus={() => setActiveInput('unitPrice')}
                onKeyDown={handleKeyDown}
                readOnly={isMobile}
                className={`w-full p-3 border-2 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-center text-lg ${activeInput === 'unitPrice' ? 'border-blue-500 dark:border-blue-400' : ''} read-only:bg-white dark:read-only:bg-gray-800`}
                placeholder="0"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">
                Seña por envase
              </label>
              <div className="text-xs text-gray-400 dark:text-gray-500 mb-1">
                {lot.container_deposit ? `Sugerida: $${lot.container_deposit}` : 'Sin seña sugerida'}
              </div>
              <input
                type="text"
                inputMode="decimal"
                value={deposit}
                onChange={(e) => handleInputChange(e, setDeposit)}
                onFocus={() => setActiveInput('deposit')}
                onKeyDown={handleKeyDown}
                readOnly={isMobile}
                className={`w-full p-3 border-2 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-center text-lg ${activeInput === 'deposit' ? 'border-blue-500 dark:border-blue-400' : ''} read-only:bg-white dark:read-only:bg-gray-800`}
                placeholder="0"
              />
            </div>
          </div>

          {/* Totales */}
          <div className="pt-4 border-t dark:border-gray-700 space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Subtotal</span>
              <span className="text-gray-900 dark:text-white">${subtotal.toLocaleString()}</span>
            </div>
            {totalDeposit > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Total señas</span>
                <span className="text-gray-900 dark:text-white">${totalDeposit.toLocaleString()}</span>
              </div>
            )}
            <div className="flex justify-between font-medium">
              <span className="text-gray-900 dark:text-white">Total</span>
              <span className="text-gray-900 dark:text-white">${(subtotal + totalDeposit).toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Teclado numérico - Solo visible en dispositivos móviles */}
        <div className="md:hidden">
          <NumericKeypad
            onInput={handleNumericInput}
            onDelete={handleDelete}
            onConfirm={handleConfirm}
            onNext={handleNext}
            hasNext={hasNextInput()}
            isLoading={isLoading}
            disabled={isConfirmDisabled}
          />
        </div>

        {/* Botón confirmar - Solo visible en desktop */}
        <div className="hidden md:block">
          <button
            onClick={handleConfirm}
            disabled={isConfirmDisabled || isLoading}
            className="w-full py-3 bg-green-500 dark:bg-green-600 text-white rounded-lg disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:text-gray-500 dark:disabled:text-gray-400 hover:bg-green-600 dark:hover:bg-green-700 transition-colors relative"
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Confirmando...
              </div>
            ) : (
              'Confirmar'
            )}
          </button>
        </div>
      </div>
    </>
  );
};

export default SaleModal; 